import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Security as SecurityIcon,
  TouchApp as TouchAppIcon,
  Update as UpdateIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import logo from '../assets/logo0.png';
import heroImage from '../assets/hero-app.png';

const Mobile = () => {
  const features = [
    {
      icon: <SpeedIcon sx={{ color: '#FFE302', fontSize: 40 }} />,
      title: "Fast Diagnostics",
      description: "Get instant AI-powered device diagnostics on your mobile"
    },
    {
      icon: <SecurityIcon sx={{ color: '#FFE302', fontSize: 40 }} />,
      title: "Secure Platform",
      description: "End-to-end encryption for all your repair data and transactions"
    },
    {
      icon: <TouchAppIcon sx={{ color: '#FFE302', fontSize: 40 }} />,
      title: "Easy to Use",
      description: "Intuitive interface designed for seamless user experience"
    },
    {
      icon: <UpdateIcon sx={{ color: '#FFE302', fontSize: 40 }} />,
      title: "Regular Updates",
      description: "Continuous improvements and new features"
    }
  ];

  const benefits = [
    "Instant access to AI diagnostics",
    "Secure spare parts marketplace",
    "24/7 chat support",
    "Real-time repair tracking",
    "Video consultation feature",
    "Cloud-based repair history"
  ];

  return (
    <Box sx={{ bgcolor: '#000000', minHeight: '100vh', color: 'white' }}>
      {/* Header with Logo */}
      <Box sx={{ 
        bgcolor: '#000000',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        py: 3
      }}>
        <Container maxWidth="lg">
          <Box
            component="img"
            src={logo}
            alt="Larvaram Logo"
            sx={{
              height: '40px',
              width: 'auto',
              filter: 'brightness(0) invert(1)'
            }}
          />
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: '#111111',
        color: 'white', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(to right, #111111 40%, rgba(0,124,63,0.1) 100%)'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" sx={{ 
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3,
                letterSpacing: '-0.02em'
              }}>
                Larvaram AI Mobile App
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 4, 
                opacity: 0.9,
                fontWeight: 300,
                lineHeight: 1.6
              }}>
                Your smart repair solution in your pocket. Download now and experience the future of device repairs.
              </Typography>
              
              {/* Added Code Bar */}
              <Box sx={{ 
                bgcolor: '#111',
                borderRadius: '8px',
                p: 2,
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    component="img"
                    src={heroImage}
                    alt="App Icon"
                    sx={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      filter: 'brightness(0) invert(1)'
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Larvaram AI
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      Download Now
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#FFE302',
                    color: '#000',
                    '&:hover': { bgcolor: '#FFD700' }
                  }}
                >
                  Get App
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AppleIcon />}
                  sx={{
                    bgcolor: '#FFE302',
                    color: '#000',
                    py: 2,
                    px: 4,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': { bgcolor: '#FFD700' }
                  }}
                >
                  Download on App Store
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AndroidIcon />}
                  sx={{
                    color: '#FFE302',
                    borderColor: '#FFE302',
                    py: 2,
                    px: 4,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': { 
                      borderColor: '#FFD700',
                      color: '#FFD700'
                    }
                  }}
                >
                  Get it on Google Play
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                position: 'relative'
              }}>
                <Box
                  component="img"
                  src={heroImage}
                  alt="App Preview"
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    transform: 'scale(1.2)',
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: '#0A0A0A', py: 15 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}>
            Powerful Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Card sx={{ 
                  bgcolor: '#000000',
                  color: 'white',
                  boxShadow: 'none',
                  height: '100%'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: 'white'
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#999' }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#000000', py: 15 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ 
            mb: 8, 
            textAlign: 'center', 
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}>
            Why Choose Our App
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ p: 3 }}>
                  <List>
                    <ListItem disableGutters>
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#FFE302' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={benefit} 
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            color: 'white',
                            fontWeight: 500
                          }
                        }} 
                      />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Download Section */}
      <Box sx={{ 
        bgcolor: '#0A0A0A', 
        py: 15,
        textAlign: 'center'
      }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 600,
            letterSpacing: '-0.02em'
          }}>
            Get Started Today
          </Typography>
          <Typography variant="h6" sx={{ 
            mb: 6, 
            color: '#999',
            fontWeight: 300
          }}>
            Join thousands of users who trust Larvaram AI for their device repairs
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AppleIcon />}
              sx={{
                bgcolor: '#FFE302',
                color: '#000',
                py: 2,
                px: 6,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': { bgcolor: '#FFD700' }
              }}
            >
              Download on App Store
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<AndroidIcon />}
              sx={{
                color: '#FFE302',
                borderColor: '#FFE302',
                py: 2,
                px: 6,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': { 
                  borderColor: '#FFD700',
                  color: '#FFD700'
                }
              }}
            >
              Get it on Google Play
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Mobile; 